import * as CL from '@design-system/component-library';
import classNames from 'classnames';
import { useCallback, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { type OpenFormContext } from '../../OpenFormAnswers.js';
import { OpenFormGridCol, OpenFormGridRow } from '../../OpenFormComponents/OpenFormGrid.js';
import { setValueOptions } from '../../OpenFormHooks/useOpenFormMethods.js';
import { Table } from '../../OpenFormHooks/useTable.js';
import type { OpenFormChoice } from '../../../../generated/api/openFormChoice.js';

export const OpenFormQuestionSelectGroup = ({
  disabled,
  required,
  label,
  name,
  multiselect,
  visible,
  context = {},
  availability,
  infoText,
}: {
  disabled: boolean;
  required: boolean;
  label: string;
  name: string;
  multiselect: boolean;
  visible: OpenFormChoice[];
  context: OpenFormContext | undefined;
  availability: Record<string, boolean>;
  infoText?: string;
}) => {
  const { setValue } = useFormContext();
  const choices = useMemo(() => new Set(context.choices), [context.choices]);

  const rows = useMemo(() => {
    return visible
      .map(({ guid, values }) => ({ available: availability[guid], choice: guid, values }))
      .sort(Table.compare('available'));
  }, [visible, availability]);

  const onClick = useCallback(
    (value?: string) => {
      if (!disabled && value) {
        if (choices.has(value)) {
          choices.delete(value);
        } else {
          !multiselect && choices.clear();
          choices.add(value);
        }
        setValue(name, Array.from(choices), setValueOptions);
      }
    },
    [disabled, name, multiselect, setValue, choices]
  );

  return !rows.length ? null : (
    <OpenFormGridRow>
      <OpenFormGridCol colWidth={6} className={classNames({ ['label--mandatory']: required })}>
        <CL.SelectGroup
          key={name}
          label={label}
          i18n_SelectGroup_infoText={infoText}
          multiselect={true}
          products={rows.map(row => ({
            active: choices.has(row.choice),
            disabled: disabled,
            id: row.choice,
            name: row.values.join('\n'),
          }))}
          type="column"
          value={Array.from(choices)}
          onClick={e => onClick((e.target as HTMLInputElement).value)}
        />
      </OpenFormGridCol>
    </OpenFormGridRow>
  );
};
